import axios, { type AxiosInstance, type AxiosError } from 'axios';
import Cookies from 'js-cookie';

interface ApiClientConfig {
    baseURL?: string;
    timeout?: number;
}

interface RequestOptions {
    /** allow per-request override of the API base URL */
    baseURL?: string;
    method: 'get' | 'post' | 'put' | 'patch' | 'delete';
    url: string;
    data?: unknown;
    formData?: boolean;
    authToken?: string;
    headers?: Record<string, string>;
    params?: Record<string, unknown>;
    timeout?: number;
}

const apiURL = 'https://api-v2.podus.org/api';
// const apiURL = 'http://localhost:9000/api';

class ApiClient {
    private client: AxiosInstance;

    constructor({ baseURL = apiURL, timeout = 10000 }: ApiClientConfig = {}) {
        this.client = axios.create({ baseURL, timeout });
    }

    public async request<T>(options: RequestOptions): Promise<T> {
        const { method, url, data, headers = {}, params, timeout, formData, baseURL: requestBaseURL } = options;

        const accessToken = Cookies.get('p-ai-accessToken');
        const bearerToken = `G@V3l2023-new-podus-v001233-SyS:${accessToken}`;

        try {
            const request = await this.client.request<T>({
                method,
                url,
                data,
                params,
                timeout,
                baseURL: requestBaseURL,
                headers: {
                    'content-type': formData ? 'multipart/form-data' : 'application/json',
                    ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
                    ...headers,
                },
            });

            return request.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError;

                if (axiosError.message === 'Network Error') {
                    return {
                        message: 'Network Error',
                        status: 0,
                    } as unknown as T;
                }

                const errorData = (axiosError.response?.data ?? {
                    message: 'Unknown error occurred',
                    status: axiosError.response?.status ?? 500,
                }) as T;
                return errorData;
            }

            throw error;
        }
    }
}

const apiClient = new ApiClient();

export type { RequestOptions };
export default apiClient;
